import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { listRules } from '@/api/rules';
import { listRuleSets } from '@/api/rule_sets';
import { Badge } from '@/components/ui/badge';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { WikiLayout, WikiSection, type WikiTocEntry } from '@/components/wiki-layout';
import { useAuth } from '../auth/AuthContext';
import type { FindingOutcome, RuleOut, RuleSetOut } from '@/api/types';

/** Active rule set for the current customer. The wiki shows only this
 *  one — older sets stay on disk for historical replay (findings
 *  reconstruct from `(rule_id, rule_version)`) but aren't browsable. */
const ACTIVE_RULE_SET = 'nvg-baseline';

/** Wiki page for the active rule set. Single card: id + version + the
 *  ordered list of rules it includes, each rule linking to its full
 *  definition. No description prose; the per-rule page carries the
 *  detail. */
export function RuleSetsPage() {
  const { isSignedIn, userKey, getIdToken } = useAuth();

  const ruleSetsQ = useQuery({
    queryKey: ['rule-sets', userKey],
    enabled: isSignedIn,
    queryFn: async () => listRuleSets((await getIdToken()) ?? ''),
  });
  const rulesQ = useQuery({
    queryKey: ['rules', userKey],
    enabled: isSignedIn,
    queryFn: async () => listRules((await getIdToken()) ?? ''),
  });

  if (!isSignedIn) {
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Not signed in</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (ruleSetsQ.isPending || rulesQ.isPending) {
    return <p className="text-brand-slate">Loading rule set…</p>;
  }
  if (ruleSetsQ.isError || rulesQ.isError) {
    console.error('rule-sets load failed:', ruleSetsQ.error ?? rulesQ.error);
    return (
      <Card className="max-w-xl border-destructive/30">
        <CardHeader>
          <CardTitle>Couldn't load rule set</CardTitle>
          <CardDescription>
            Something went wrong. Try refreshing. Details in the browser console.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const activeSet = ruleSetsQ.data.items.find((s) => s.id === ACTIVE_RULE_SET);
  if (!activeSet) {
    return (
      <Card className="max-w-xl border-destructive/30">
        <CardHeader>
          <CardTitle>Rule set not found</CardTitle>
          <CardDescription>
            <span className="font-mono">{ACTIVE_RULE_SET}</span> is not in the catalog. Check the
            server logs.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const rulesById = new Map<string, RuleOut>(rulesQ.data.items.map((r) => [r.id, r]));
  const toc: WikiTocEntry[] = [{ id: activeSet.id, label: activeSet.id }];

  return (
    <WikiLayout
      title="Rule set"
      description="The bundle of rules every Now Vertical audit run evaluates."
      toc={toc}
    >
      <RuleSetEntry ruleSet={activeSet} rulesById={rulesById} />
    </WikiLayout>
  );
}

function OutcomeBadge({ outcome }: { outcome: FindingOutcome }) {
  if (outcome === 'recovery') {
    return <Badge className="border-transparent bg-emerald-600 text-white">recovery</Badge>;
  }
  return <Badge className="border-transparent bg-amber-600 text-white">compliance risk</Badge>;
}

function RuleSetEntry({
  ruleSet,
  rulesById,
}: {
  ruleSet: RuleSetOut;
  rulesById: Map<string, RuleOut>;
}) {
  const outcomeCounts: Record<FindingOutcome, number> = { recovery: 0, compliance_risk: 0 };
  for (const ref of ruleSet.rules) {
    const rule = rulesById.get(ref.id);
    if (rule) outcomeCounts[rule.outcome] += 1;
  }
  const ruleCount = ruleSet.rules.length;
  return (
    <article
      id={ruleSet.id}
      className="grid scroll-mt-8 gap-5 rounded-lg border-l-4 border-l-brand-royal border-y border-r border-y-border border-r-border bg-card px-5 py-5 shadow-sm sm:px-6 sm:py-6"
    >
      <header className="grid gap-3 border-b border-border/60 pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="font-mono text-sm">
            {ruleSet.id}
          </Badge>
          <span className="font-mono text-xs text-brand-slate/70">v{ruleSet.version}</span>
          <span className="ml-auto flex flex-wrap items-center gap-1.5 text-xs text-brand-slate">
            <span className="font-semibold text-brand-navy">{ruleCount}</span>
            <span>rule{ruleCount === 1 ? '' : 's'}</span>
            {outcomeCounts.recovery > 0 && (
              <Badge tone="positive" soft pill className="ml-1">
                {outcomeCounts.recovery} recovery
              </Badge>
            )}
            {outcomeCounts.compliance_risk > 0 && (
              <Badge tone="caution" soft pill>
                {outcomeCounts.compliance_risk} compliance risk
              </Badge>
            )}
          </span>
        </div>
      </header>

      <WikiSection title="Rules in this set">
        <ul className="grid gap-2">
          {ruleSet.rules.map((ref) => {
            const rule = rulesById.get(ref.id);
            return (
              <li
                key={`${ruleSet.id}-rule-${ref.id}`}
                className="rounded-md border border-border bg-secondary/20 px-3 py-2 transition-colors hover:border-brand-royal/40 hover:bg-secondary/40"
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  {rule && (
                    <Badge variant="outline" className="font-mono text-[11px]">
                      {rule.aa_code}
                    </Badge>
                  )}
                  {rule && <OutcomeBadge outcome={rule.outcome} />}
                  <Link
                    to="/rules"
                    hash={ref.id}
                    className="font-medium text-brand-royal underline-offset-4 hover:underline"
                  >
                    {rule ? rule.title : ref.id}
                  </Link>
                  <span className="ml-auto font-mono text-[11px] text-brand-slate/70">
                    {ref.id}
                    {ref.version != null && `@v${ref.version}`}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      </WikiSection>
    </article>
  );
}
